// contexts/SuperAdminContext.tsx
import React, { createContext, useContext } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { API_BASE_URL } from "../constants/config";
import { getAndSanitize } from "../shared/utils/sanitization";

interface PanelData {
  name: string;
  username: string;
  password?: string;
  admins: {
    userId: string;
  }[];
  baseUrl: string;
  token: string;
}
interface EditAdminParams {
  id: string;
  data: any;
}
interface NotificationConfigData {
  id?: string;
  panelEndpointId: string;
  notificationType: string;
  useFlow: boolean;
  flowId?: string;
  messageTemplate: string;
}
interface SuperAdminContextType {
  users: any[];
  panels: any[];
  wallets: any[];
  isLoading: boolean;
  createAdmin: (data: any) => void;
  createPanel: (data: PanelData) => void;
  createWallet: (data: any) => void;
  togglePanel: (variables: { id: string; isActive: boolean }) => void;
  toggleWallet: (variables: { id: string; isActive: boolean }) => void;
  deleteAdmin: (id: string) => void;
  deletePanel: (id: string) => void;
  deleteWallet: (id: string) => void;
  editAdmin: (id: string, data: any) => void;
  editPanel: (variables: { id: string; data: any }) => void;
  editWallet: (variables: { id: string; data: any }) => void;
  createDistribuitor: (data: any) => void;
  editDistribuitor: (params: { id: string; data: any }) => void;
  deleteDistribuitor: (id: string) => void;
  getAdminConfig: (adminId: string) => Promise<any>;

  createAdinfinity: (data: {
    username: string;
    password: string;
    emails: { email: string; password: string }[];
  }) => void;
  endpoints: any[];
  createEndpoint: (data: any) => void;
  editEndpoint: (params: { id: string; data: any }) => void;
  deleteEndpoint: (id: string) => void;
  sessions: any[];
  sessionsLoading: boolean;
  logoutSession: (id: string) => void;
  logoutAllUserSessions: (userId: string) => void;
  banUser: (userId: string) => void;
  banAdmin: (adminId: string) => void; // Add this line
  unbanAdmin: (adminId: string) => void; // Add this line
  notificationConfigs: any[];
  notificationTypes: { value: string; label: string }[];
  createNotificationConfig: (data: NotificationConfigData) => void;
  updateNotificationConfig: (params: {
    configId: string;
    data: Partial<NotificationConfigData>;
  }) => void;
  deleteNotificationConfig: (configId: string) => void;
  getNotificationConfigsByEndpoint: (endpointId: string) => Promise<any[]>;
}
const SuperAdminContext = createContext<SuperAdminContextType | undefined>(
  undefined
);

export const SuperAdminProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const queryClient = useQueryClient();
  const token = getAndSanitize("token");

  const headers = {
    Authorization: `Bearer ${token}`,
  };

  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/api/auth/users`, {
        headers,
      });
      return response.data.data;
    },
  });

  const { data: endpoints } = useQuery({
    queryKey: ["panelEndpoints"],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/api/auth/endpoints`, {
        headers,
      });
      return response.data.data;
    },
  });

  const createEndpoint = useMutation({
    mutationFn: async (data: any) => {
      return axios.post(`${API_BASE_URL}/api/auth/endpoints`, data, {
        headers,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["panelEndpoints"] });
    },
  });

  const editEndpoint = useMutation({
    mutationFn: async ({ id, data }: any) => {
      return axios.put(`${API_BASE_URL}/api/auth/endpoints/${id}`, data, {
        headers,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["panelEndpoints"] });
    },
  });

  const deleteEndpoint = useMutation({
    mutationFn: async (id: string) => {
      return axios.delete(`${API_BASE_URL}/api/auth/endpoints/${id}`, {
        headers,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["panelEndpoints"] });
    },
  });
  // Añadir dentro del hook SuperAdminProvider
  const { data: notificationTypes = [] } = useQuery({
    queryKey: ["notificationTypes"],
    queryFn: async () => {
      const response = await axios.get(
        `${API_BASE_URL}/api/auth/notification-types`,
        { headers }
      );
      return response.data.data;
    },
  });

  const createNotificationConfig = useMutation({
    mutationFn: async (data: NotificationConfigData) => {
      const response = await axios.post(`${API_BASE_URL}/api/auth/notification-configs`, data, {
        headers,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notificationConfigs"] });
      queryClient.invalidateQueries({ queryKey: ["panelEndpoints"] });
      // Forzar invalidación después de un breve delay
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["notificationConfigs"] });
      }, 200);
    },
    onError: (error) => {
      console.error("[SuperAdmin] Error creating notification config:", error);
    }
  });

  const updateNotificationConfig = useMutation({
    mutationFn: async ({
      configId,
      data,
    }: {
      configId: string;
      data: Partial<NotificationConfigData>;
    }) => {
      const response = await axios.put(
        `${API_BASE_URL}/api/auth/notification-configs/${configId}`,
        data,
        { headers }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notificationConfigs"] });
      queryClient.invalidateQueries({ queryKey: ["panelEndpoints"] });
    },
    onError: (error) => {
      console.error("[SuperAdmin] Error updating notification config:", error);
    }
  });

  const deleteNotificationConfig = useMutation({
    mutationFn: async (configId: string) => {
      return axios.delete(
        `${API_BASE_URL}/api/auth/notification-configs/${configId}`,
        { headers }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notificationConfigs"] });
      queryClient.invalidateQueries({ queryKey: ["panelEndpoints"] });
    },
  });

  const getNotificationConfigsByEndpoint = async (endpointId: string) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/auth/endpoints/${endpointId}/notification-configs`,
        { headers }
      );
      return response.data.data;
    } catch (error: any) {
      console.error("[SuperAdmin] Error getting notification configs:", error);
      return [];
    }
  };

  const getAdminConfig = async (adminId: string) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/auth/admin/${adminId}/config`,
        { headers }
      );
      return response.data.data;
    } catch (error) {
      console.error("[SuperAdmin] Error getting admin config:", error);
      return { pixelDepositTracking: false };
    }
  };

  const { data: panels, isLoading: panelsLoading } = useQuery({
    queryKey: ["panels"],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/api/auth/panels`, {
        headers,
      });
      return response.data.data;
    },
  });
  const createAdinfinity = useMutation({
    mutationFn: async (data: any) => {
      return axios.post(`${API_BASE_URL}/api/auth/adinfinity`, data, {
        headers,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
  const { data: wallets, isLoading: walletsLoading } = useQuery({
    queryKey: ["wallets"],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/api/auth/wallets`, {
        headers,
      });
      return response.data.data;
    },
  });

  const createAdmin = useMutation({
    mutationFn: async (data: any) => {
      return axios.post(`${API_BASE_URL}/api/auth/admin`, data, { headers });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });

  const createDistribuitor = useMutation({
    mutationFn: async (data: any) => {
      return axios.post(`${API_BASE_URL}/api/auth/distribuitor`, data, {
        headers,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
  const editDistribuitor = useMutation({
    mutationFn: async (params: { id: string; data: any }) => {
      return axios.put(
        `${API_BASE_URL}/api/auth/distribuitor/${params.id}`,
        params.data,
        { headers }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
  const deleteDistribuitor = useMutation({
    mutationFn: async (id: string) => {
      return axios.delete(`${API_BASE_URL}/api/auth/distribuitor/${id}`, {
        headers,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });

  const createPanel = useMutation({
    mutationFn: async (data: PanelData) => {

      // Validar que los admins tengan el formato correcto
      if (!data.admins || !Array.isArray(data.admins) || data.admins.length === 0) {
        // Obtener el ID del usuario actual si no hay admins seleccionados
        const currentUserId = getAndSanitize("userId");
        if (!currentUserId) {
          throw new Error("No se pudo obtener el ID del usuario actual");
        }
        
        // Usar el usuario actual como admin por defecto
        data.admins = [{ userId: currentUserId }];
      }

      // Asegurar que los admins tengan formato válido
      const validAdmins = data.admins.filter((admin) => !!admin.userId);
      
      if (validAdmins.length === 0) {
        const currentUserId = getAndSanitize("userId");
        if (!currentUserId) {
          throw new Error("No se pudo obtener el ID del usuario actual ni hay admins válidos");
        }
        validAdmins.push({ userId: currentUserId });
      }

      const payload = {
        name: data.name,
        username: data.username,
        password: data.password,
        admins: validAdmins,
        baseUrl: data.baseUrl,
        token: data.token,
      };


      const response = await axios.post(
        `${API_BASE_URL}/api/auth/panel`,
        payload,
        { headers }
      );

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["panels"] });
    },
    onError: (error) => {
      console.error("[SuperAdminContext] Panel creation failed:", error);
    },
  });

  const createWallet = useMutation({
    mutationFn: async (data: any) => {

      return axios.post(
        `${API_BASE_URL}/api/auth/wallet`,
        {
          name: data.name,
          mpUserId: data.mpUserId,
          token: data.token,
          username: data.username,
          password: data.password,
          description: data.description,
          userId: data.userId,
        },
        { headers }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wallets"] });
    },
    onError: (error) => {
      console.error("[SuperAdminContext] Wallet creation failed:", error);
    },
  });

  const togglePanel = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      return axios.patch(
        `${API_BASE_URL}/api/auth/panels/${id}/toggle`,
        { isActive },
        { headers }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["panels"] });
    },
  });

  const toggleWallet = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      return axios.patch(
        `${API_BASE_URL}/api/auth/wallets/${id}/toggle`,
        { isActive },
        { headers }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wallets"] });
    },
  });

  const deletePanel = useMutation({
    mutationFn: async (id: string) => {
      const token = getAndSanitize("token");
      return axios.delete(`${API_BASE_URL}/api/auth/panel/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["panels"] });
    },
  });

  const deleteWallet = useMutation({
    mutationFn: async (id: string) => {
      const token = getAndSanitize("token");
      return axios.delete(`${API_BASE_URL}/api/auth/wallet/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wallets"] });
    },
  });

  const deleteAdmin = useMutation({
    mutationFn: async (id: string) => {
      const token = getAndSanitize("token");
      return axios.delete(`${API_BASE_URL}/api/auth/admin/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });

  const editAdmin = useMutation({
    mutationFn: async (params: EditAdminParams) => {
      return axios.put(
        `${API_BASE_URL}/api/auth/admin/${params.id}`,
        params.data,
        { headers }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
  const editPanel = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.put(
        `${API_BASE_URL}/api/auth/panel/${data.id}`,
        data.data,
        { headers }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["panels"] });
    },
    onError: (error) => {
      console.error("[SuperAdminContext] Error editing panel:", error);
    },
  });

  // In SuperAdminContext.tsx
  const editWallet = useMutation({
    mutationFn: async (params: { id: string; data: any }) => {
      return axios.put(
        `${API_BASE_URL}/api/auth/wallet/${params.id}`,
        params.data,
        { headers }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wallets"] });
    },
    onError: (error) => {
      console.error("[Wallet] Edit failed:", error);
    },
  });

  const { data: sessions, isLoading: sessionsLoading } = useQuery({
    queryKey: ["sessions"],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/api/auth/sessions`, {
        headers,
      });
      return response.data.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const logoutSession = useMutation({
    mutationFn: async (id: string) => {
      return axios.delete(`${API_BASE_URL}/api/auth/sessions/${id}`, {
        headers,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
    },
  });

  const logoutAllUserSessions = useMutation({
    mutationFn: async (userId: string) => {
      return axios.delete(`${API_BASE_URL}/api/auth/users/${userId}/sessions`, {
        headers,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
    },
  });

  const banUser = useMutation({
    mutationFn: async (userId: string) => {
      return axios.post(
        `${API_BASE_URL}/api/auth/users/${userId}/ban`,
        {},
        { headers }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });

  // Add banAdmin mutation
  const banAdmin = useMutation({
    mutationFn: async (adminId: string) => {
      return axios.post(
        `${API_BASE_URL}/api/auth/users/${adminId}/ban`,
        {},
        { headers }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });

  // Add unbanAdmin mutation
  const unbanAdmin = useMutation({
    mutationFn: async (adminId: string) => {
      return axios.post(
        `${API_BASE_URL}/api/auth/users/${adminId}/activate`,
        {},
        { headers }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });

const value = {
  users: users || [],
  panels: panels || [],
  wallets: wallets || [],
  isLoading: usersLoading || panelsLoading || walletsLoading,
  createAdmin: createAdmin.mutate,
  createPanel: createPanel.mutate,
  createWallet: createWallet.mutate,
  togglePanel: togglePanel.mutate,
  toggleWallet: toggleWallet.mutate,
  deleteAdmin: deleteAdmin.mutate,
  deletePanel: deletePanel.mutate,
  deleteWallet: deleteWallet.mutate,
  editAdmin: (id, data) => editAdmin.mutate({ id, data }),
  editPanel: editPanel.mutate,
  editWallet: editWallet.mutate,
  sessions: sessions || [],
  sessionsLoading,
  logoutSession: logoutSession.mutate,
  logoutAllUserSessions: logoutAllUserSessions.mutate,
  banUser: banUser.mutate,
  banAdmin: banAdmin.mutate,
  unbanAdmin: unbanAdmin.mutate,
  createDistribuitor: createDistribuitor.mutate,
  editDistribuitor: editDistribuitor.mutate,
  deleteDistribuitor: deleteDistribuitor.mutate,
  createAdinfinity: createAdinfinity.mutate,
  endpoints: endpoints || [],
  createEndpoint: createEndpoint.mutate,
  editEndpoint: editEndpoint.mutate,
  deleteEndpoint: deleteEndpoint.mutate,
  notificationConfigs: [],
  notificationTypes: notificationTypes.map((type: any) => ({ value: type.value, label: type.label })),
  createNotificationConfig: createNotificationConfig.mutate,
  updateNotificationConfig: updateNotificationConfig.mutate,
  deleteNotificationConfig: deleteNotificationConfig.mutate,
  getNotificationConfigsByEndpoint,
  getAdminConfig
};
  return (
    <SuperAdminContext.Provider value={value}>
      {children}
    </SuperAdminContext.Provider>
  );
};

export const useSuperAdmin = () => {
  const context = useContext(SuperAdminContext);
  if (context === undefined) {
    throw new Error("useSuperAdmin must be used within a SuperAdminProvider");
  }
  return context;
};